import { Link } from "react-router-dom";
import { Globe } from "lucide-react";
import { useLanguage } from "@/lib/i18n";
import { useLandingCopy } from "./LandingContext";
import { AppStoreButton } from "./AppStoreButton";

/**
 * Minimal top bar for the ad landing page: just the logo and a language
 * switch. No full menu — nothing should distract from the single conversion
 * goal (App Store click).
 */
export function LandingNav() {
  const { switchLanguagePath, getLocalizedPath } = useLanguage();
  const copy = useLandingCopy();

  return (
    <header className="absolute top-0 left-0 right-0 z-50">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
        <Link to={getLocalizedPath("/")} className="flex items-center" aria-label="ReFound">
          <img src="/logo-test-myreceipt-17.png" alt="ReFound" className="h-6 sm:h-7" />
        </Link>
        <div className="flex items-center gap-2">
          <Link
            to={switchLanguagePath()}
            title={copy.switchTitle}
            className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
          >
            <Globe className="h-4 w-4" />
            <span>{copy.switchLabel}</span>
          </Link>
          {/* Persistent CTA on desktop (mobile uses the sticky bottom bar) */}
          <AppStoreButton
            location="nav"
            label={copy.ctaShort}
            variant="button"
            className="hidden px-4 py-2 text-sm lg:inline-flex"
          />
        </div>
      </div>
    </header>
  );
}

/** Minimal footer: legal links + copyright. Keeps the page compliant and tidy. */
export function LandingFooter() {
  const { getLocalizedPath } = useLanguage();
  const copy = useLandingCopy();
  const year = 2026;

  return (
    <footer className="border-t border-border bg-background">
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:justify-between sm:text-left">
          <div className="flex items-center gap-3">
            <img src="/logo-test-myreceipt-17.png" alt="ReFound" className="h-6" />
            <span className="text-sm text-muted-foreground">{copy.footer.tagline}</span>
          </div>
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <Link
              to={getLocalizedPath("/privacy")}
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              {copy.footer.privacy}
            </Link>
            <Link
              to={getLocalizedPath("/terms")}
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              {copy.footer.terms}
            </Link>
            <Link
              to={getLocalizedPath("/support")}
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              {copy.footer.support}
            </Link>
          </nav>
        </div>
        <p className="mt-6 text-center text-xs text-muted-foreground/70 sm:text-left">
          © {year} ReFound. {copy.footer.rights}
        </p>
      </div>
    </footer>
  );
}
